import { clientConfig } from '@/lib/session';

/**
 * The GIF picker, powered by KLIPY. Searches go straight from the browser to
 * KLIPY (its terms require that), using its Tenor-compatible API. A sent GIF is
 * just its link; everyone else sees it through ClanFire's image proxy.
 */
export interface GifResult {
  id: string;
  title: string;
  /** Small, for the picker grid. */
  previewUrl: string;
  /** What gets sent. */
  url: string;
  width: number;
  height: number;
}

const API = 'https://api.klipy.com/v2';
/** Only links to KLIPY's own media hosts are sent as GIFs. */
export const GIF_URL_RE = /^https:\/\/static\d*\.klipy\.com\/[^\s<>"]+$/;

export const gifsEnabled = () => !!clientConfig.gifApiKey;

type Media = { url?: unknown; dims?: unknown; width?: unknown; height?: unknown };
const asMedia = (value: unknown): Media | null => (value && typeof value === 'object' ? (value as Media) : null);
const urlOf = (m: Media | null) => (typeof m?.url === 'string' && GIF_URL_RE.test(m.url) ? m.url : null);
const sizeOf = (m: Media | null): [number, number] => {
  if (Array.isArray(m?.dims) && m.dims.length === 2) return [Number(m.dims[0]) || 1, Number(m.dims[1]) || 1];
  return [Number(m?.width) || 1, Number(m?.height) || 1];
};

/**
 * Reads KLIPY's Tenor-style answer (results[].media_formats) or its own
 * (data.data[].file.{sm,md,hd}.gif). Anything not on KLIPY's hosts is dropped.
 */
export function parseGifResults(body: unknown): GifResult[] {
  const root = (body ?? {}) as { results?: unknown[]; data?: { data?: unknown[] } };
  const out: GifResult[] = [];
  for (const raw of root.results ?? root.data?.data ?? []) {
    const item = (raw ?? {}) as Record<string, unknown>;
    const formats = item.media_formats as Record<string, unknown> | undefined;
    const file = item.file as Record<string, Record<string, unknown>> | undefined;
    const pick = (...keys: string[]) => {
      for (const key of keys) {
        const media = formats ? asMedia(formats[key]) : asMedia(file?.[key]?.gif);
        if (urlOf(media)) return media;
      }
      return null;
    };
    const preview = formats ? pick('tinygif', 'nanogif', 'mediumgif', 'gif') : pick('sm', 'xs', 'md', 'hd');
    const full = formats ? pick('mediumgif', 'gif', 'tinygif') : pick('md', 'hd', 'sm');
    if (!preview || !full) continue;
    const [width, height] = sizeOf(full);
    out.push({
      id: String(item.id ?? item.slug ?? urlOf(full)),
      title: String(item.content_description ?? item.title ?? 'GIF'),
      previewUrl: urlOf(preview)!,
      url: urlOf(full)!,
      width,
      height,
    });
  }
  return out;
}

/** Trending GIFs for an empty query, otherwise search results. */
export async function findGifs(query: string, signal?: AbortSignal): Promise<GifResult[]> {
  const key = clientConfig.gifApiKey;
  if (!key) return [];
  const q = query.trim();
  const params = new URLSearchParams({ key, client_key: 'clanfire', limit: '30', media_filter: 'tinygif,mediumgif,gif', contentfilter: 'medium' });
  if (q) params.set('q', q);
  const res = await fetch(`${API}/${q ? 'search' : 'featured'}?${params}`, { signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
  if (!res.ok) throw new Error(`KLIPY answered ${res.status}`);
  return parseGifResults(await res.json());
}

/** Whether a message is nothing but a GIF link (shown as just the GIF). */
export function isGifOnly(content: string): boolean {
  return GIF_URL_RE.test(content.trim());
}
